import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const COMBINATORS = [
 { name: 'Promise.all', line: 'All must succeed — rejects on first failure (fail-fast).' },
 { name: 'Promise.allSettled', line: 'Waits for everyone — returns each outcome, never rejects.' },
 { name: 'Promise.race', line: 'First to settle wins — success OR failure.' },
 { name: 'Promise.any', line: 'First to succeed wins — failures ignored until all fail.' },
];

export const JSPromisesAdvanced2: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Promises Advanced (Part 2)</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    The rest of the combinator family — <code>allSettled</code>, <code>race</code>, <code>any</code>, and <code>AggregateError</code> — plus sequential vs parallel, timeouts, concurrency control, and worker pools.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="18 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>In Part 1 we saw <Code>Promise.all</Code> is fail-fast. Revisit the notification flow after an order is placed — email, SMS, and push:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `await Promise.all([sendEmail(), sendSMS(), sendPush()]);` }]} defaultLanguage="javascript" id="pa2-problem" />
   <Para>If the SMS provider is down, <Code>Promise.all</Code> rejects — and the whole workflow looks failed, even though the email and push <Mark>succeeded</Mark>. Do we really want that? Not always. Different workflows need different coordination strategies.</Para>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why Existing Solutions Fail</SectionHeading>
   <Para><Code>Promise.all</Code> answers exactly one question: &quot;did everything succeed?&quot; Real systems also need to ask:</Para>
   <div className="flex flex-wrap gap-2 mb-4">{['What happened to each task?', 'Which response came first?', 'Which one succeeded first?', 'What if they all fail?'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>JavaScript provides a combinator for each. Here&apos;s the full family at a glance:</Para>
   <div className="my-4 space-y-2">
    {COMBINATORS.map((c) => (
     <div key={c.name} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5">
      <span className="font-mono text-[13px] font-bold text-amber-700 dark:text-amber-300">{c.name}()</span>
      <span className="text-[13px] text-gray-600 dark:text-gray-300"> — {c.line}</span>
     </div>
    ))}
   </div>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Code>allSettled</Code>, <Code>race</Code>, and <Code>any</Code> are promise combinators that coordinate many promises with <Mark>different settling rules</Mark> — waiting for all outcomes, taking the first to settle, or taking the first to succeed.</p>
   </div>
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>The Core Idea: Promise.allSettled()</SectionHeading>
   <Para><Code>Promise.allSettled</Code> waits for <Mark>every</Mark> promise to finish regardless of success or failure — there is no fail-fast:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const results = await Promise.allSettled([\n  sendEmail(),\n  sendSMS(),\n  sendPush(),\n]);` }]} defaultLanguage="javascript" id="pa2-allsettled" />
   <Output>{`[
  { status: "fulfilled", value: "Email Sent" },
  { status: "rejected",  reason: "SMS Failed" },
  { status: "fulfilled", value: "Push Sent" }
]`}</Output>
   <Para>Every outcome is returned — nothing is lost. You decide how to handle the partial failure.</Para>
  </section>

  {/* 5. Internal Working */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <TwoUp
    left={{ title: 'Promise.all', items: ['First failure → reject immediately', 'Other results discarded', 'One error reason'] }}
    right={{ title: 'Promise.allSettled', items: ['Wait for everyone', 'Collect all results', 'Return {status, value/reason}[]'] }}
   />
   <Para>Because it collects a status object for each input, <Code>allSettled</Code> never throws — even when some promises reject.</Para>
  </section>

  {/* 6. Visual Flow */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `Email → Success ┐\nSMS   → Failure ├─ allSettled → [fulfilled, rejected, fulfilled]\nPush  → Success ┘   (waits for ALL, returns every outcome)` }]} defaultLanguage="text" id="pa2-visual" />
  </section>

  {/* 7. Simple Example: allSettled in production */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example: Notification Audit</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const results = await Promise.allSettled([\n  sendEmail(),\n  sendSMS(),\n  sendPush(),\n]);\n\nsaveAuditLogs(results);` }]} defaultLanguage="javascript" id="pa2-audit" />
   <Para>Now Ops sees &quot;Email success, SMS failed, Push success&quot; instead of a vague &quot;Notification failed.&quot;</Para>
   <TwoUp
    left={{ title: 'Use allSettled for', items: ['Notifications', 'Reporting', 'Batch jobs', 'Analytics', 'Data sync'] }}
    right={{ title: 'Avoid it for', items: ['Authentication', 'Payment processing', 'Permission checks', '(every step must succeed)'] }}
   />
  </section>

  {/* 8. Deep Example: race + timeout */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Promise.race() &amp; Timeouts</SectionHeading>
   <Para>An external payment provider normally answers in <Code>500ms</Code>, but today it takes 45 seconds — and your API hangs. <Code>Promise.race</Code> returns the <Mark>first promise to settle</Mark> (success or failure), which makes timeouts possible:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function timeout(ms) {\n  return new Promise((_, reject) => {\n    setTimeout(() => reject(new Error("Timeout")), ms);\n  });\n}\n\nawait Promise.race([\n  fetchUser(),\n  timeout(5000),\n]);\n// resolves if fetchUser wins; rejects "Timeout" if 5s passes first` }]} defaultLanguage="javascript" id="pa2-timeout" />
   <Para>Without timeouts you get hung requests, memory buildup, thread-pool saturation, and poor UX. Mature systems never trust an external service to respond indefinitely.</Para>
   <Callout tone="warning" title="Interview trap: does race cancel the losers?">
    No. <Code>Promise.race</Code> only decides <Mark>which result is used</Mark>. The other promises keep running to completion — JavaScript has no built-in promise cancellation.
   </Callout>
  </section>

  {/* 9. Production Example: any + multi-region */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Example: Promise.any() &amp; Multi-Region</SectionHeading>
   <Para><Code>Promise.any</Code> returns the first promise to <Mark>fulfill</Mark>, ignoring failures. Perfect when you have redundant sources and only need one to answer — e.g. servers in multiple regions:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const data = await Promise.any([\n  usRegion(),\n  euRegion(),\n  asiaRegion(),\n]);\n// first region to succeed wins; earlier failures are ignored` }]} defaultLanguage="javascript" id="pa2-any" />
   <div className="flex flex-wrap gap-2">{['Lower latency', 'Regional failover', 'Higher availability'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>This is the <Mark>hedged requests</Mark> pattern used in distributed systems.</Para>
  </section>

  {/* 10. Engineering Tradeoffs: race vs any + AggregateError */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs</SectionHeading>
   <SubHeading>race vs any — a common mix-up</SubHeading>
   <TwoUp
    left={{ title: 'Promise.race', items: ['First settled wins', 'Success OR failure', 'A rejection can win'] }}
    right={{ title: 'Promise.any', items: ['First success wins', 'Failures ignored', 'Rejects only if ALL fail'] }}
   />
   <CodeBlock snippets={[{ language: 'javascript', code: `Promise.race([Promise.reject("Error"), Promise.resolve("Success")]);\n// → rejects with "Error"  (failure settled first)\n\nPromise.any([Promise.reject("Error"), Promise.resolve("Success")]);\n// → resolves with "Success"  (failures ignored)` }]} defaultLanguage="javascript" id="pa2-racevany" />
   <SubHeading>AggregateError: when every promise fails</SubHeading>
   <Para>If all promises passed to <Code>Promise.any</Code> reject, it throws an <Code>AggregateError</Code> that groups every failure instead of losing them:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `try {\n  await Promise.any([\n    Promise.reject("US"),\n    Promise.reject("EU"),\n    Promise.reject("India"),\n  ]);\n} catch (error) {\n  console.log(error.errors); // ["US", "EU", "India"]\n}` }]} defaultLanguage="javascript" id="pa2-aggregate" />
  </section>

  {/* 11. Performance Considerations: sequential vs parallel + concurrency */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations</SectionHeading>
   <SubHeading>Sequential vs parallel — parallel isn&apos;t always better</SubHeading>
   <Para>Run in parallel only when tasks are <Mark>independent</Mark>. When each step needs the previous result, sequential is <em>required</em>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Sequential — REQUIRED (dependencies)\nconst user = await getUser();\nconst orders = await getOrders(user.id);   // needs user.id\nconst payments = await getPayments(orders); // needs orders\n\n// Parallel — CORRECT (independent)\nconst [profile, offers, wallet] = await Promise.all([\n  getProfile(), getOffers(), getWallet(),\n]);` }]} defaultLanguage="javascript" id="pa2-seqvpar" />
   <SubHeading>Concurrency control</SubHeading>
   <Para>Can <Code>Promise.all</Code> handle 100,000 requests? Technically yes; in practice it triggers memory explosions, rate limits, connection exhaustion, and CPU spikes. Bound it with <Mark>batching</Mark>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `for (let i = 0; i < users.length; i += 100) {\n  const batch = users.slice(i, i + 100);\n  await Promise.all(batch.map(sendEmail)); // 100 at a time\n}` }]} defaultLanguage="javascript" id="pa2-batch" />
   <Para>The <Mark>worker pool</Mark> generalizes this: with 10 workers and 1000 jobs, only 10 run at once and each worker picks the next job when free. It&apos;s the model behind BullMQ, RabbitMQ consumers, thread pools, and job schedulers.</Para>
   <Callout tone="info" title="I/O-bound vs CPU-bound">
    These combinators speed up <Mark>waiting</Mark> (network, DB, file I/O), not computation. Video encoding, compression, ML, and encryption need worker threads, separate processes, or distributed computing — not <Code>Promise.all</Code>.
   </Callout>
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="Promise.all for dependent tasks">Parallelizing steps that actually depend on each other&apos;s output.</Callout>
   <Callout tone="warning" title="Unbounded parallelism">Launching thousands of requests at once instead of batching.</Callout>
   <Callout tone="warning" title="No timeout handling">Trusting an external service to respond, leaving requests hung forever.</Callout>
   <Callout tone="warning" title="Confusing race and any">Expecting <Code>race</Code> to ignore failures — only <Code>any</Code> does.</Callout>
   <Callout tone="warning" title="Assuming race cancels losers">The losing promises keep running; race only picks which result is used.</Callout>
  </section>

  {/* 13. Debugging Stories */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The hang that took down the pool">
    An API call to a third-party provider had no timeout. When the provider slowed to 45s, requests piled up and the connection pool saturated, taking unrelated endpoints down with it. Wrapping the call in <Code>Promise.race([call(), timeout(5000)])</Code> contained the blast radius.
   </Callout>
   <Callout tone="info" title="The any that swallowed everything">
    A multi-region fetch used <Code>Promise.any</Code> but &quot;returned nothing&quot; during an outage. All regions had failed, so it threw an <Code>AggregateError</Code> that wasn&apos;t being inspected. Logging <Code>error.errors</Code> revealed all three regional failures at once.
   </Callout>
  </section>

  {/* 14. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={14}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['all vs allSettled?', 'any vs race?', 'What is fail-fast?', 'What is AggregateError?', 'Does Promise.all create threads?', 'When to avoid Promise.all?', 'How to implement timeouts?', 'How to process 1M jobs safely?', 'What is concurrency control?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="What is the difference between Promise.race and Promise.any?">
    <Code>race</Code> settles with the first promise to settle — a rejection can win. <Code>any</Code> resolves with the first to <em>fulfill</em> and only rejects (with an <Code>AggregateError</Code>) if every promise fails.
   </QA>
   <QA q="Does Promise.race cancel the other promises?">
    No. JavaScript has no built-in promise cancellation. <Code>race</Code> only chooses which result you receive; the losing promises continue running to completion.
   </QA>
   <QA q="What is AggregateError?">
    The error thrown by <Code>Promise.any</Code> when all promises reject. It groups every individual failure in an <Code>error.errors</Code> array so no failure information is lost.
   </QA>
   <QA q="How do you safely process a million async jobs?">
    Not with one giant <Code>Promise.all</Code>. Bound concurrency with batching or a worker pool so only N run at a time — protecting memory, connections, and rate limits while keeping throughput high.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={15}>Mental Model</SectionHeading>
   <Para>Imagine ordering from five restaurants:</Para>
   <div className="my-4 space-y-2">
    {[
     ['Promise.all', 'Need food from all — one failure fails the whole order.'],
     ['Promise.allSettled', 'Tell me what happened at every restaurant.'],
     ['Promise.race', 'First restaurant to respond wins (even a "we\u2019re closed").'],
     ['Promise.any', 'First restaurant to successfully accept my order wins.'],
    ].map(([name, line]) => (
     <div key={name} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5">
      <span className="font-mono text-[13px] font-bold text-amber-700 dark:text-amber-300">{name}</span>
      <span className="text-[13px] text-gray-600 dark:text-gray-300"> — {line}</span>
     </div>
    ))}
   </div>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Promise combinators coordinate multiple async operations with different strategies — wait for all results (<code>all</code>), collect all outcomes (<code>allSettled</code>), take the fastest (<code>race</code>), or take the first success (<code>any</code>).
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={17}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    <Code>all</Code> is for &quot;every task must succeed&quot;; <Code>allSettled</Code> for &quot;every result matters&quot;; <Code>race</Code> for fastest-result and timeouts; <Code>any</Code> for redundancy and first-success. Senior engineers go past syntax into concurrency control, batching, timeouts, and production-safe orchestration — choosing the combinator that fits the workload.
   </Callout>
  </section>
 </div>
);

export default JSPromisesAdvanced2;
